import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookButton } from './components/BookButton';
import { BottomSection } from './components/BottomSection';
import { Container } from './components/Container';
import { Header } from './components/Header';
import { MenuThemeProvider } from './components/MenuThemeProvider';
import { SearchBar } from './components/SearchBar';
import { MenuItems as BurgerMenuItems } from './components/burger-menu/MenuItems';
import { MenuItems as IoTMenuItems } from './components/iot-menu/MenuItems';
import TriggerButtons from './components/trigger-buttons/TriggerButtons';
import { MenuContext } from './context';
import { EMenuOpenType, Props } from './types';
import { DEFAULT_COLORS, DEFAULT_SETTINGS, getColors } from './utils';
import { useDebounce } from 'usehooks-ts';

const WIDTH = 300; // Setting BM to a constant width for now. Variable width is not working properly

export function MenuWrapper(props: Props) {
  const {
    open,
    settings,
    minimapHeight,
    contact,
    address,
    whitelabel,
    rtl,
    sendToGA,
    sendTagToGA,
    parentMenus,
    currentSid,
    iotGroups,
    iotDevices,
    providers,
    iotDevicesTypes,
    hideTriggerButton,
    openType,
    showBothTriggers,
    translation,
    activeMenuId,
    moveToMPLabels,
    loadModel,
    onBMClose,
    onBMOpen,
    onIoTOpen,
    onIoTClose,
    handleLock,
    handleOpenCamera,
    handleMoveToLock,
    onWidthChange,
    handleItemClickLuma,
    favouriteCameras,
    libraryIconsBaseUrl,
    awsIconUrl,
  } = props;
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 300);
  const [searchBar, setSearchBar] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState(parentMenus?.[0] || null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedSubItem, setSelectedSubItem] = useState(null);

  const isBM = useMemo(() => openType === EMenuOpenType.BM, [openType]);
  const isIoT = useMemo(() => openType === EMenuOpenType.IoT, [openType]);

  // Needed for the openOnHover feature
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const colors = useMemo(
    () => getColors(props.colors, settings.transparent),
    [props.colors, settings.transparent],
  );

  const toggleSearchBar = useCallback(() => {
    if (searchBar) {
      setSearchText('');
    }

    setSearchBar(!searchBar);
  }, [searchBar]);

  useEffect(() => {
    onWidthChange?.(open ? WIDTH : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hideTriggerBtns = useMemo(
    () => hideTriggerButton || (settings.alwaysOpen && (!onBMClose || !onIoTClose)),
    [hideTriggerButton, onBMClose, onIoTClose, settings.alwaysOpen],
  );

  useEffect(() => {
    if (selectedMenu) {
      const updatedSelectedMenu = parentMenus?.find((parentMenu) => selectedMenu?.id === parentMenu.id);
      setSelectedMenu(updatedSelectedMenu);
    }
  }, [parentMenus]);

  useEffect(() => {
    // Keep selectedSubItem synchronized with updates in parentMenus (subMenus/subSubMenus structure)
    if (!selectedSubItem || !parentMenus) return;

    const findById = (id: any) => {
      for (const pm of parentMenus || []) {
        for (const m of pm.menus || []) {
          if (m && m.id === id) return m;
          for (const sm of m?.subMenus || []) {
            if (sm && sm.id === id) return sm;
            for (const ssm of sm?.subSubMenus || []) {
              if (ssm && ssm.id === id) return ssm;
            }
          }
        }
      }
      return null;
    };

    const updated = findById(selectedSubItem.id);

    if (!updated) {
      // Selected sub item no longer exists in the updated structure
      setSelectedSubItem(null);
      return;
    }

    const prevSubMenusLen = Array.isArray(selectedSubItem?.subMenus) ? selectedSubItem.subMenus.length : 0;
    const prevSubSubMenusLen = Array.isArray(selectedSubItem?.subSubMenus) ? selectedSubItem.subSubMenus.length : 0;

    const newSubMenusLen = Array.isArray(updated?.subMenus) ? updated.subMenus.length : 0;
    const newSubSubMenusLen = Array.isArray(updated?.subSubMenus) ? updated.subSubMenus.length : 0;

    if (prevSubMenusLen !== newSubMenusLen || prevSubSubMenusLen !== newSubSubMenusLen) {
      setSelectedSubItem(updated);
    }
  }, [parentMenus, selectedSubItem?.id, selectedSubItem?.subMenus?.length, selectedSubItem?.subSubMenus?.length]);

  return (
    <MenuContext.Provider
      value={{
        colors: colors || DEFAULT_COLORS,
        settings: settings || DEFAULT_SETTINGS,
        rtl,
        open,
        openType,
        translation: translation || {},
      }}
    >
      <MenuThemeProvider>
        <Container
          width={WIDTH}
          minimapHeight={minimapHeight}
          menuIsOpen={menuIsOpen}
          setMenuIsOpen={setMenuIsOpen}
          onClose={onBMClose || onIoTClose}
        >
          {(showBothTriggers || !hideTriggerBtns) && (
            <TriggerButtons
              onBMOpen={onBMOpen}
              onIoTOpen={onIoTOpen}
              onBMClose={onBMClose}
              onIoTClose={onIoTClose}
              menuIsOpen={menuIsOpen}
              setMenuIsOpen={setMenuIsOpen}
              bmItems={parentMenus}
              handleSelectMenu={(menu) => {
                setSelectedMenu(menu);
                setSelectedMenuItem(null);
              }}
              libraryIconsBaseUrl={libraryIconsBaseUrl}
              awsIconUrl={awsIconUrl}
            />
          )}

          <Header
            onClose={
              !settings.alwaysOpen || settings.fullScreenMobile
                ? onBMClose || onIoTClose
                : undefined
            }
            toggleSearchBar={settings.search ? toggleSearchBar : undefined}
            title={selectedSubItem ? selectedSubItem?.title : selectedMenuItem ? selectedMenuItem?.title : selectedMenu?.title}
            handleBack={() => {
              if (selectedSubItem) {
                setSelectedSubItem(null);
              } else {
                setSelectedMenuItem(null);
              }
            }}
            isChild={!!selectedMenuItem || !!selectedSubItem}
          />

          {selectedMenu?.searchEnabled && (
            <SearchBar
              searchText={searchText}
              setSearchText={setSearchText}
              searchBar={searchBar}
            />
          )}

          {props.book && props.book.label && isBM && (
            <BookButton url={props.book.url} label={props.book.label} sendToGA={sendToGA} />
          )}

          {isIoT && (
            <IoTMenuItems
              devicesTypes={iotDevicesTypes}
              groups={iotGroups}
              devices={iotDevices}
              searchText={debouncedSearch}
              handleLock={handleLock}
              handleOpenCamera={handleOpenCamera}
              handleMoveToLock={handleMoveToLock}
              providers={providers}
              favouriteCameras={favouriteCameras}
            />
          )}

          {isBM && (
            <BurgerMenuItems
              items={
                selectedSubItem
                  ? selectedSubItem.subSubMenus || []
                  : selectedMenuItem
                    ? selectedMenuItem.subMenus || []
                    : selectedMenu?.menus || []
              }
              view={selectedMenu?.typeView}
              sendToGA={sendToGA}
              sendTagToGA={sendTagToGA}
              searchText={debouncedSearch}
              currentSid={currentSid}
              activeMenuId={activeMenuId}
              moveToMPLabels={moveToMPLabels}
              loadModel={loadModel}
              handleItemClickLuma={handleItemClickLuma}
              libraryIconsBaseUrl={libraryIconsBaseUrl}
              awsIconUrl={awsIconUrl}
              handleSetMenuItem={(item) => setSelectedMenuItem(item)}
              handleSetSubItem={(item) => setSelectedSubItem(item)}
            />
          )}

          <BottomSection
            isBM={isBM}
            whitelabel={whitelabel}
            contact={contact}
            address={address}
            sendToGA={sendToGA}
          />
        </Container>
      </MenuThemeProvider>
    </MenuContext.Provider>
  );
}
